import { Component, OnInit, ComponentFactoryResolver } from '@angular/core';
import {
	AbstractModal,
	BroadcastEventModel,
	BroadcastService,
	ModalButtonModel,
	ModalButtonType,
	ModalService,
	ModalSize,
} from '@cv-invision/core';
// Services
import { ClientIdentifierListService } from '../service/client-identifier-list.service';
import { Subject, takeUntil } from 'rxjs';
import { AddEditClientIdentifier } from './add-edit-client-identifier.component';
import { ADD_EDIT_CLIENT_IDENTIFIER, ClientIdentifierModel } from '../model/client-identifier.model';
import { ConfirmationService, MessageService } from 'primeng/api';

@Component({
	selector: 'cv-client-identifier-list',
	styleUrls: ['./client-identifier-list.component.scss'],
	templateUrl: './client-identifier-list.component.html',
	providers: [ConfirmationService, MessageService]
})
export class ClientIdentifierList extends AbstractModal implements OnInit {
	public clientIdentifiers: any = [];
	public integrationName: string;
	public saving: boolean;

	private atComponentDestroy: Subject<any> = new Subject<any>();

	constructor(
		private service: ClientIdentifierListService,
		private modalService: ModalService,
		private componentFactoryResolver: ComponentFactoryResolver,
		private broadcastService: BroadcastService,
		private confirmationService: ConfirmationService,
		private messageService: MessageService,
	) { 
		super();
	}

	ngOnInit(): void {
		this.integrationName = this.data.integrationName;

		const cancelButton: ModalButtonModel = {
			name: 'close',
			text: 'Close',
			class: 'btn-sm',
			type: ModalButtonType.ACTION,
			action: this.onCancel.bind(this)
		};

		this.buttons.push(cancelButton);

		this.loadData();
		this.registerAddEditClientIdentifierListener();
	}

	/**
	 * Register the listener for the add/edit client identifier modal
	 */
	private registerAddEditClientIdentifierListener(): void {
		this.broadcastService.on(ADD_EDIT_CLIENT_IDENTIFIER, (broadcast: BroadcastEventModel) => {
			this.loadData();

			if (broadcast.event.isEditing) {
				this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Client Identifier was updated' });
			} else {
				this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Client Identifier was added' });
			}
		});
	}

	/**
	 * Get the data from the service
	 */
	private loadData() {
		this.service
			.getClientIdentifiersByIntegrationName(this.integrationName)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				this.clientIdentifiers = result;
			});
	}

	/**
	 * Add or edit a client identifier record
	 */
	public addEditClientIdentifier(clientIdentifier: ClientIdentifierModel = null): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: AddEditClientIdentifier,
				data: {
					clientIdentifier,
					integrationName: this.integrationName
				},
				modalConfiguration: {
					title: `${clientIdentifier ? 'Edit' : 'Add'} Client Identifier`,
					modalSize: ModalSize.XL
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe();
	}

	/**
	 * Show delete confirmation
	 * @param clientIdentifier 
	 */
	public onShowDeleteConfirm(clientIdentifier: ClientIdentifierModel) {
		this.confirmationService.confirm({
			message: 'Are you sure that you want to proceed?',
			header: 'Confirmation',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.deleteClientIdentifier(clientIdentifier);
			},
		});
	}

	/**
	 * Delete a client identifier record
	 * @param clientIdentifier 
	 */
	private deleteClientIdentifier(clientIdentifier: ClientIdentifierModel): void {
		this.service
			.deleteClientIdentifier(this.integrationName, clientIdentifier)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				this.loadData();
				this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Client Identifier was deleted' });
			});
	}

	onDismiss(): void {
		super.onCancel();
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.complete();
	}
}
